import { Controller } from "@hotwired/stimulus"
import ClipBuilderApi from "../services/clip_builder_api"

/**
 * Clip Builder Controller (coordinator)
 *
 * The slim core of a clip card: section toggles, audio preview, asset uploads,
 * and inline field/checkbox/slider updates. Overlay editors moved to
 * `clip-overlays`; preview / render-picker / generate / remove-video moved to
 * `clip-video-source`. All three controllers mount on the same card root and
 * share the ClipBuilderApi request layer.
 */
export default class extends Controller {
  static values = { uploadUrl: String, updateUrl: String, segmentIndex: Number }
  static targets = ["imageInput", "audioInput", "videoInput", "audioPreview", "effectsSection", "overlaysSection"]

  get api() {
    return new ClipBuilderApi({
      uploadUrl: this.uploadUrlValue,
      updateUrl: this.updateUrlValue,
      segmentIndex: this.segmentIndexValue,
    })
  }

  toggleSection(event) {
    const section = event.currentTarget.dataset.section
    const target = section === "effects" ? this.effectsSectionTarget : this.overlaysSectionTarget
    if (target) target.classList.toggle("hidden")
  }

  toggleAudio() {
    if (!this.hasAudioPreviewTarget) return

    const audio = this.audioPreviewTarget
    if (audio.paused) {
      audio.play()
    } else {
      audio.pause()
      audio.currentTime = 0
    }
  }

  uploadImage() { this.imageInputTarget.click() }
  uploadAudio() { this.audioInputTarget.click() }
  uploadVideo() { this.videoInputTarget.click() }

  handleImageUpload(event) { this._upload(event, "image") }
  handleAudioUpload(event) { this._upload(event, "audio") }
  handleVideoUpload(event) { this._upload(event, "video") }

  async _upload(event, assetType) {
    const file = event.target.files[0]
    if (!file) return

    try {
      const { response, data } = await this.api.uploadAsset(file, assetType)
      if (response.ok) {
        window.location.reload()
      } else {
        alert(data.error || `Failed to upload ${assetType}`)
      }
    } catch {
      alert(`Failed to upload ${assetType}`)
    }
  }

  updateField(event) {
    this._patchField(event.target.dataset.field, event.target.value)
  }

  toggleCheckbox(event) {
    const field = event.target.dataset.field
    const value = event.target.checked

    // Visual hint: dim the Veo volume slider when its checkbox is off.
    if (field === "use_video_audio") {
      const slider = event.target.closest(".flex")?.querySelector("input[type='range']")
      if (slider) slider.classList.toggle("opacity-40", !value)
    }

    this._patchField(field, value)
  }

  previewVeoVolume(event) {
    const label = event.target.parentElement.querySelector("[data-veo-volume-label]")
    if (label) label.textContent = `${event.target.value}%`
  }

  setVeoVolume(event) {
    const volume = parseInt(event.target.value, 10) / 100
    this._patchField("video_audio_volume", volume)
  }

  setTargetDuration(event) {
    const secs = parseInt(event.currentTarget.dataset.targetDuration, 10)
    if (![4, 6, 8].includes(secs)) return

    // Visual: swap selected styles across the button group.
    const group = event.currentTarget.parentElement
    group.querySelectorAll("button[data-target-duration]").forEach((btn) => {
      const active = parseInt(btn.dataset.targetDuration, 10) === secs
      btn.classList.toggle("bg-indigo-600", active)
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("border-indigo-600", active)
      btn.classList.toggle("bg-gray-50", !active)
      btn.classList.toggle("text-gray-600", !active)
      btn.classList.toggle("border-gray-200", !active)
    })

    this._patchField("target_duration_seconds", secs)
  }

  async _patchField(field, value) {
    try {
      await this.api.updateSegment({ [field]: value })
    } catch {
      // Silent fail for field updates
    }
  }
}
